import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["activeTrials", "expiredTrials", "isMultiTrial", "hideBackground", "upgradeData", "upgradeData", "onSuccess", "shouldHideSurvey", "canViewNewSeats", "isTX0120Variant", "isTX0120Fetched", "isEligibleForTX0120"];
import Raven from 'raven-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiNameToTrialIds, trialIdsWithTrialGuide, upgradeProductToTrialIds } from 'self-service-api/constants/Trials';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { EXPLORED_FEATURES_KEY, putJsonBlob, resolveJsonBlob } from '../../common/api/jsonBlobAPI';
import ActivationDuplicateErrorModal from '../../common/components/activation/ActivationDuplicateErrorModal';
import ActivationErrorModal from '../../common/components/activation/ActivationErrorModal';
import { validTrialExplorationFeatures } from '../../common/constants/validTrialExplorationFeatures';
import { track } from '../../common/eventTracking/tracker';
import { navigateTo } from '../../common/navigation/url';
import { TRIAL } from '../../purchaseMotions/PurchaseMotionTypes';
import { getTrialGuideUrl, getTrialGuideUrlFromApiName, refresh, refreshLockedFeature } from '../../utils/links';
import { trackFailedApiNameMap, trackPurchaseMotionFailure, trackTrialActivation } from '../../utils/metricUtils';
import TrialActivationSuccessContainer from './TrialActivationSuccessContainer';
import { activateTrial } from './activateTrial';
import { jsx as _jsx } from "react/jsx-runtime";
const statuses = {
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success'
};
const HIDE_SUBTITLE_SCREENS = ['products-plans', 'service-hub-paywall'];
const TrialActivationModalContainer = props => {
  const [trialActivationRequestStatus, setTrialActivationRequestStatus] = useState(statuses.LOADING);
  const [showTrialLoadingState, setShowTrialLoadingState] = useState(!!props.shouldHideSurvey);
  const [error, setError] = useState(null);
  const [exploredTrialFeatures, setExploredTrialFeatures] = useState({});
  const isMounted = useRef(false);
  const {
      activeTrials,
      expiredTrials,
      isMultiTrial,
      hideBackground = false,
      upgradeData,
      upgradeData: {
        apiName,
        app,
        screen,
        uniqueId,
        upgradeProduct,
        feature
      },
      onSuccess,
      shouldHideSurvey,
      canViewNewSeats,
      isTX0120Variant = false,
      isTX0120Fetched = false,
      isEligibleForTX0120 = false
    } = props,
    successModalPassthroughProps = _objectWithoutPropertiesLoose(props, _excluded);
  const showSubtitle = !HIDE_SUBTITLE_SCREENS.includes(screen);
  const defaultOnSuccess = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    } else {
      const eligibleForNewSeats = canViewNewSeats && apiName;
      const getTrialId = () => {
        if (eligibleForNewSeats && apiNameToTrialIds[apiName]) {
          return apiNameToTrialIds[apiName];
        } else {
          if (eligibleForNewSeats) {
            trackFailedApiNameMap('getTrialId', apiName, upgradeProduct);
            Raven.captureException(new Error(`failed to get trial id from apiName ${apiName} in getTrialId`));
          }
          return upgradeProductToTrialIds[upgradeProduct];
        }
      };
      const trialId = getTrialId();
      refreshLockedFeature();
      let putJsonBlobPromise;
      if (apiName && feature && validTrialExplorationFeatures.includes(feature)) {
        const updatedExploredTrialFeatures = Object.assign({}, exploredTrialFeatures, {
          [apiName]: [...(exploredTrialFeatures[apiName] || []), feature]
        });
        putJsonBlobPromise = putJsonBlob(EXPLORED_FEATURES_KEY, updatedExploredTrialFeatures);
      } else {
        putJsonBlobPromise = Promise.resolve();
      }
      putJsonBlobPromise.then(() => {
        if (trialId && trialIdsWithTrialGuide.includes(trialId)) {
          const source = `${app}-${screen}-${uniqueId}`;
          navigateTo(eligibleForNewSeats ? getTrialGuideUrlFromApiName(apiName, source) : getTrialGuideUrl(upgradeProduct, source));
        } else {
          refresh();
        }
      }).catch(() => {
        Raven.captureException(new Error('failed to put explored trial features'));
      });
    }
  }, [onSuccess, app, screen, uniqueId, upgradeProduct, apiName, canViewNewSeats, feature, exploredTrialFeatures]);
  useEffect(() => {
    isMounted.current = true;
    track('trialModalInteraction', Object.assign({
      action: 'opened modal container'
    }, getUpgradeDataTrackingProperties(upgradeData), {
      trialId: upgradeData.trialId,
      duration: upgradeData.duration
    }));
    let activeTrialFetchErrored = false;
    activateTrial({
      upgradeData,
      isMultiTrial,
      activeTrials,
      expiredTrials
    }).catch(err => {
      activeTrialFetchErrored = true;
      if (isMounted.current) {
        trackPurchaseMotionFailure({
          uniqueId: upgradeData.uniqueId,
          apiName: upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct],
          purchaseMotion: TRIAL
        });
        setTrialActivationRequestStatus(statuses.ERROR);
        setError(err);
      }
    }).finally(() => {
      if (!activeTrialFetchErrored) {
        resolveJsonBlob(EXPLORED_FEATURES_KEY).then(exploredFeatureResponse => {
          if (isMounted.current) {
            setExploredTrialFeatures(exploredFeatureResponse);
          }
        }).catch(err => {
          console.error('Failed to fetch explored features', err);
        });
        if (isMounted.current) {
          setTrialActivationRequestStatus(statuses.SUCCESS);
          setShowTrialLoadingState(false);
          trackTrialActivation(upgradeData.app, upgradeData.upgradeProduct);
        }
      }
    });
    return () => {
      isMounted.current = false;
    };
  }, [activeTrials, expiredTrials, isMultiTrial, upgradeData]);
  switch (trialActivationRequestStatus) {
    case statuses.LOADING:
      return null;
    case statuses.ERROR:
      track('trialModalInteraction', Object.assign({
        action: 'opening activation error modal'
      }, getUpgradeDataTrackingProperties(upgradeData), {
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));
      if (error && error.responseJSON) {
        const activateError = error.responseJSON;
        if (activateError.message && activateError.message.match(/^Cannot start duplicate trial/)) {
          return /*#__PURE__*/_jsx(ActivationDuplicateErrorModal, {
            onSuccess: defaultOnSuccess,
            upgradeData: upgradeData
          });
        }
      }
      return /*#__PURE__*/_jsx(ActivationErrorModal, {});
    case statuses.SUCCESS:
      track('trialModalInteraction', Object.assign({
        action: 'opening activation success modal'
      }, getUpgradeDataTrackingProperties(upgradeData), {
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));
      return /*#__PURE__*/_jsx(TrialActivationSuccessContainer, Object.assign({
        onSuccess: defaultOnSuccess,
        activeTrials: activeTrials,
        expiredTrials: expiredTrials,
        isMultiTrial: isMultiTrial,
        hideBackground: hideBackground,
        upgradeData: upgradeData,
        showTrialLoadingState: showTrialLoadingState,
        showSubtitle: showSubtitle,
        shouldHideSurvey: shouldHideSurvey,
        canViewNewSeats: canViewNewSeats,
        isTX0120Variant: isTX0120Variant,
        isTX0120Fetched: isTX0120Fetched,
        isEligibleForTX0120: isEligibleForTX0120
      }, successModalPassthroughProps));
    default:
      return null;
  }
};
export default TrialActivationModalContainer;